import $ from 'jquery';
import { Modernizr } from '@lib/modernizr';
import { enableProdMode } from '@angular/core';

import { initFeatureFlagStatsig, StatsigService } from 'mt-shared';

import { defaultHeaders, overrideAuthToken, overrideGeoTabAuthToken } from 'mt-shared';
import { loadGTMScript } from '@lib/google-tag-manager';
import { loadGoogleAnalytics } from '@lib/google-analytics';
import { setSessionData } from 'mt-shared';
import { initPendo } from '@lib/pendo';
import { configDatadogLogs, initDatadogRum, initPartialDatadogRum, logDatadogError } from '@lib/datadog';
import { registerAppPolyFills } from '@lib/polyfill';
import { isDriverSafetyView } from './mobile/driver-safety/util';
import { mapLocale } from '@lib/map-locale';
import { LoadTimeLogger, MARKERS, MEASURES_NAMES } from 'mt-shared';
import {
  logIndexEJSLoadTime,
  measureValidateCallTime,
  clearValidateCallMarkers,
  measurePromiseAllDuration,
  measureBootToPromiseDuration,
  measureBootToValidateStartDuration,
  measureValidateResponseEventDuration,
} from '@lib/performance-logging-utils';
import { initializeUserLibs, initializeNonUserLibs } from '@app/deferredLibsInitializer';

import {
  IResponseUser,
  IResponseCompany,
  IResponseCompanyConnection,
  IPartialUserInfo,
} from 'mt-shared';

import { PERMISSIONS } from '@lib/permissions';
import { clearAllCookies, clearDashboardStorage } from '@lib/logout-utils';
import { clearDomainProbeCookies } from '@lib/clear-domain-probe-cookies';
import { isLiveShareView } from '@app/public/live-share-view/is-live-share-view';
import {
  isPublicUrlAccess,
  CookieKey,
  setCookie,
  getCookie,
  isProduction,
  isStaging,
  isLocalPreview,
  isPreview,
  getApiUrl,
} from 'mt-shared';
import { getPartialUserInfo, isPermissionAllowed } from './boot-utils';
import { isIOSOrIPadOS, isAndroid } from '@lib/utils';
import { getInstallerAppStoreRedirectUrl } from '@lib/installer-app-store-redirect';

import { loadSemantic } from '@lib/semantic/load';
import './styles/boot.scss';
import './styles/less/theme.less';

logIndexEJSLoadTime();
registerAppPolyFills();

// Purge accumulated `_gd<timestamp>` domain-probe cookies before they bloat the
// header sent to subdomains and break the live-share host with HTTP 431. This runs
// on the dashboard (not the tracking page) so the jar is already trimmed before the
// user opens the public Preview link — the 431 happens on the tracking host's
// initial document request, before any script there can run. TSSD-32472.
if (!isLiveShareView()) {
  clearDomainProbeCookies();
}

const API_URL: string = getApiUrl();

// Eagerly load semantic so that it's parsed and ready when AppModule is to be loaded.
// This avoids a FOUC which happens when semanticCss and AppModule are loaded in parallel.
let semanticPromise: Promise<any>;

const isSematicEnabled: boolean = !(isDriverSafetyView);

if (isSematicEnabled) {
  semanticPromise = loadSemantic();
}

const performanceLogger: LoadTimeLogger = new LoadTimeLogger();

const isPublicCardApplicationView: boolean = window.location.href.includes('apply-cards');
export const sessionValidateURL: string = `/api/w2/sessions/validate`;
const settings = {
  method: 'GET',
  url: `${API_URL}${sessionValidateURL}`,
  headers: defaultHeaders,
};
if (isPublicCardApplicationView) {
  settings['data'] = { cards_user: true };
}

function setSupportCenterCookies(user) {
  setCookie(CookieKey.APEX_USER_NAME, `${user.first_name} ${user.last_name}`);
  setCookie(CookieKey.APEX_USER_EMAIL, user.email);
  setCookie(CookieKey.APEX_USER_PHONE, user.phone);
}

async function init(loadAppFn, data) {
  const user: IResponseUser = data.user;

  if (isProduction()) {
    setSupportCenterCookies(user);
  }

  setSessionData(user);

  const companyConnection: IResponseCompanyConnection = user.company_connection || {} as IResponseCompanyConnection;
  const company: IResponseCompany = companyConnection.company || {} as IResponseCompany;

  window.addEventListener('load', function () {
    window.performance.mark('window_loaded');
    initializeUserLibs();
    initializeNonUserLibs();
  });
  if (!isPublicCardApplicationView) {
    initPendo(
      user.id,
      user.email,
      user.role,
      user.survey_data ? user.survey_data.company_role : '',
      companyConnection.role,
      user.created_at,
      user.first_name,
      user.last_name,
      user.cycle,
      mapLocale(user.language),
      isPermissionAllowed(companyConnection, PERMISSIONS.SHOP_MANAGE),
      company.id,
      company.name,
      company.company_id,
      company.industry,
      company.freight_carried,
      company.cycle,
      company.enabled_features,
      company.eld_enabled_drivers,
      company.sf_account_status,
      company.state,
      company.subscription_plan,
      company.composite_fleet_size,
      company.annotation_tag_enabled,
      company.dashboard_upsell_pro_trial_start_date,
      company.dashboard_upsell_pro_trial_end_date,
      company.shop_eligible,
      company.ecomm_eligibility,
      'fleet-view',
    );
  }

  configDatadogLogs(user, company);
  if (isProduction() || isStaging() || isPreview()) {
    initDatadogRum(user, company);
  }

  const statsigPromise = initFeatureFlagStatsig(user, company).then(() => new StatsigService());
  const promiseArray = [statsigPromise];

  if (isSematicEnabled) promiseArray.push(semanticPromise);

  window.performance.mark(MARKERS.BOOT_TS_PROMISE_ALL_START);
  measureBootToPromiseDuration();

  Promise.all(promiseArray).then(([featureFlagService]) => {
    window.performance.mark(MARKERS.BOOT_TS_PROMISE_ALL_END);
    measurePromiseAllDuration();
    return loadAppFn(user, featureFlagService);
  });
}

function redirect(xhr) {
  // Clearing all cookies before redirecting to login page.
  // This is done to clear the US session if user logs in
  // to EU session and vice versa.
  const ERROR_STATUS_CODES = [400, 401, 403, 404, 408, 500, 501, 502, 503, 504, 511];
  if (!isLocalPreview() && !isProduction() && xhr && ERROR_STATUS_CODES.includes(xhr.status)) {
    try {
      const partialUserInfo: IPartialUserInfo = getPartialUserInfo();
      initPartialDatadogRum({
        userId: partialUserInfo.userId,
        userRole: partialUserInfo.userRole,
        companyId: partialUserInfo.companyId,
        compositeFleetSize: partialUserInfo.compositeFleetSize,
        userEmail: partialUserInfo.userEmail,
      });
      logDatadogError(xhr?.responseJSON?.error || xhr?.error || 'Validation error', {});
    } catch (error) {
      console.error('Error logging validation error to Datadog', error);
    }
  }
  // An installer who just set their password via the mobile CIAM flow has no
  // dashboard session and no use for the web login page — send them to the
  // Fleet App's store listing instead. Everyone else (no installer cookie,
  // or on desktop) falls through to the existing login redirect, unchanged.
  const installerAppStoreUrl = getInstallerAppStoreRedirectUrl(!!getCookie(CookieKey.INSTALLER_ACCESS_TOKEN), isIOSOrIPadOS, isAndroid);
  if (installerAppStoreUrl) {
    window.location.href = installerAppStoreUrl;
    return;
  }

  clearAllCookies();
  clearDashboardStorage();

  const redirectUrl = xhr.responseJSON?.redirect_url || xhr.redirect_url;
  const returnUrl = encodeURIComponent(window.location.toString());
  const separator = redirectUrl.indexOf('?') !== -1 ? '&' : '?';
  window.location.href = `${redirectUrl}${separator}return_url=${returnUrl}`;
}

function sendValidationRequest(successCb, failureCb) {
  clearValidateCallMarkers();
  window.performance.mark(MARKERS.VALIDATE_CALL_START);
  $.ajax(settings).done(successCb).fail(failureCb).always(() => measureValidateCallTime(true));
}

function validateEventHandler(success): void {
  const validationData = window['validationData'];
  const validationError = window['validationError'];
  let geotabsAuthToken;
  measureBootToValidateStartDuration();
  try {
    geotabsAuthToken = authTokenGeneratedFromGeotabIframe();
  } catch {
    console.error(`Something went wrong: Geotab's SSO not working`);
  }
  if (!isLocalPreview()) {
    enableProdMode();

    if (geotabsAuthToken) {
      overrideGeoTabAuthToken(decodeURIComponent(geotabsAuthToken));
      sendValidationRequest(success, redirect);
      measureBootToValidateStartDuration();
      return;
    }
    if (!geotabsAuthToken) measureValidateCallTime();
    if (validationData) success(validationData);
    else if (validationError) redirect(validationError);
  } else {
    if (validationData) {
      success(validationData);
      measureValidateCallTime();
    }
    else if (validationError) {
      (() => {
        const authToken = geotabsAuthToken || (window as any).decodeURIComponent(window.prompt('Auth Token'));
        if (authToken) {
          overrideAuthToken(authToken);
          sendValidationRequest(success, redirect);
        }
      })();
    }
  }
}

function validateSession(loadAppFn, byBassSessionValidation = false) {
  if (byBassSessionValidation) {
    if (!isLocalPreview()) enableProdMode();
    if (!isSematicEnabled) {
      loadAppFn(null);
    } else {
      semanticPromise.then(() => loadAppFn(null));
    }
  } else {
    const success = init.bind(undefined, loadAppFn);
    const validationData = window['validationData'];
    const validationError = window['validationError'];

    if (validationData || validationError) {
      validateEventHandler(success);
      performanceLogger.addLog(MEASURES_NAMES.VALIDATE_RESPONSE_EVENT_TIMER, 0);
      performanceLogger.flushPageLoadTimeLogs([MEASURES_NAMES.VALIDATE_RESPONSE_EVENT_TIMER]);
    } else {
      window.performance.mark(MARKERS.VALIDATE_RESPONSE_EVENT_TIMER_START);
      window.addEventListener('validation-done', (event: Event) => {
        window.performance.mark(MARKERS.VALIDATE_RESPONSE_EVENT_TIMER_END);
        measureValidateResponseEventDuration();
        validateEventHandler(success);
      }, { once: true });
    }
  }
}

export function isBrowserCompatible(): boolean {
  return Modernizr.websockets && Modernizr.flexbox && Modernizr.es6array && Modernizr.intl;
}

function isPublicUser(): boolean {
  const isUserPublic: boolean = isPublicUrlAccess();
  if (isUserPublic) {
    initFeatureFlagStatsig({ id: '' } as any, null);
  }
  return isUserPublic;
}

export function boot(loadAppFn): void {
  if (isBrowserCompatible()) {
    validateSession(loadAppFn, isPublicUser());
    loadGoogleAnalytics();
    loadGTMScript();
  } else {
    document.getElementById('app-loader').style.display = 'none';
    document.getElementById('incompatible-browser').style.display = 'table';
    (document.getElementById('incompatible-browser-img') as HTMLImageElement).src = '/incompatible-browser-banner.png';
  }
}

function authTokenGeneratedFromGeotabIframe(): string {
  // Get the fragment identifier from the URL (everything after the hash)
  const fragment = window.location.hash;

  // Create a regular expression to match the geotab_auth_token parameter
  const tokenRegex = /geotab_auth_token=([^&]+)/;

  // Use the regular expression to extract the token from the fragment
  const matches = fragment.match(tokenRegex);
  return matches?.length >= 2 ? matches[1] : undefined;
}
